import type { ReactNode } from 'react';
import { useContext, useLayoutEffect } from 'react';
import { SettingsContext, ThemeContext } from '@/state/contexts';
import { darkColors, lightColors } from '@/theme/colors';

function toKebabCase(str: string): string {
  return str.replace(/([A-Z])/g, '-$1').toLowerCase();
}

export function CSSVariablesProvider({ children }: { children: ReactNode }) {
  const { darkTheme } = useContext(ThemeContext);

  useLayoutEffect(() => {
    const colors = darkTheme ? darkColors : lightColors;
    const root = document.documentElement;

    const cssUpdates: string[] = [];

    Object.entries(colors).forEach(([key, value]) => {
      const cssVar = `--${toKebabCase(key)}`;
      cssUpdates.push(`${cssVar}:${value}`);
    });

    // Aliases for existing component compatibility
    cssUpdates.push(
      `--bg-secondary:${colors.bgElevated}`,
      `--text-secondary:${colors.textSec}`,
      `--border-color:${colors.border}`
    );

    // Shell tokens (explicit aliases)
    cssUpdates.push(
      `--shell-bg:${colors.shellBg}`,
      `--shell-text-primary:${colors.shellTextPrimary}`,
      `--shell-text-muted:${colors.shellTextMuted}`,
      `--shell-border:${colors.shellBorder}`,
      `--shell-hover:${colors.shellHover}`,
      `--shell-selected:${colors.shellSelected}`,
      `--shell-text:${colors.shellText}`,
      `--shell-text-hover:${colors.shellTextHover}`,
      `--shell-text-selected:${colors.shellTextSelected}`
    );

    // Sub-sidebar tokens
    cssUpdates.push(
      `--sub-sidebar-bg:${colors.subSidebarBg}`,
      `--sub-sidebar-border:${colors.subSidebarBorder}`,
      `--sub-sidebar-surface-1:${colors.subSidebarSurface1}`,
      `--sub-sidebar-surface-2:${colors.subSidebarSurface2}`
    );

    // Priority tokens (clinical safety)
    cssUpdates.push(
      `--priority-stat:${colors.priorityStat}`,
      `--priority-stat-bg:${colors.priorityStatBg}`,
      `--priority-urgent:${colors.priorityUrgent}`,
      `--priority-urgent-bg:${colors.priorityUrgentBg}`,
      `--priority-routine:${colors.priorityRoutine}`,
      `--priority-routine-bg:${colors.priorityRoutineBg}`,
      `--sla-overdue:${colors.priorityStat}`,
      `--sla-overdue-bg:${colors.priorityStatBg}`,
      `--sla-due-soon:${colors.priorityUrgent}`,
      `--sla-due-soon-bg:${colors.priorityUrgentBg}`,
      `--sla-on-time:${colors.success}`,
      `--sla-on-time-bg:rgba(6, 146, 121, 0.15)`
    );

    // Viewer tokens
    cssUpdates.push(
      `--viewer-bg:${colors.viewerBg}`,
      `--filmstrip-bg:${colors.filmstripBg}`
    );

    // Motion duration variables (updated)
    cssUpdates.push(
      '--motion-duration-fast:0.15s',
      '--motion-duration-mid:0.2s',
      '--motion-duration-slow:0.3s',
      '--transition-fast:0.15s ease',
      '--transition-base:0.2s ease',
      '--transition-slow:0.3s ease'
    );

    // Layout variables
    cssUpdates.push(
      '--sidebar-width:82px',
      '--sub-sidebar-width:280px',
      '--header-height:64px',
      '--height-touch:44px',
      '--height-icon:36px'
    );

    // Z-index scale
    cssUpdates.push(
      '--z-base:0',
      '--z-dropdown:1000',
      '--z-sticky:1020',
      '--z-modal-backdrop:1040',
      '--z-modal:1050',
      '--z-tooltip:1060',
      '--z-notification:1070'
    );

    // Line heights
    cssUpdates.push(
      '--leading-tight:1.25',
      '--leading-normal:1.5',
      '--leading-relaxed:1.75'
    );

    // Report-specific and AI variables
    if (darkTheme) {
      cssUpdates.push(
        `--accent-gradient:${colors.accentGradient}`,
        `--ai-accent:${colors.aiAccent}`,
        `--ai-glow:${colors.aiGlow}`,
        `--finding-bg:${colors.findingBg}`,
        `--shadow-focus:${colors.shadowFocus}`
      );
    } else {
      cssUpdates.push(
        `--accent-gradient:${colors.accentGradient}`,
        `--ai-accent:${colors.aiAccent}`,
        `--ai-glow:${colors.aiGlow}`,
        `--finding-bg:${colors.findingBg}`,
        `--shadow-focus:${colors.shadowFocus}`
      );
    }

    // Apply all CSS variables in a single batch using cssText
    // This triggers only ONE style recalculation instead of 30+
    root.style.cssText += cssUpdates.join(';');

    // Set data-theme attribute separately (doesn't trigger recalc)
    root.setAttribute('data-theme', darkTheme ? 'dark' : 'light');
  }, [darkTheme]);

  // Compact mode — override spacing variables via data attribute (same pattern as dark/light)
  const { settings } = useContext(SettingsContext);
  useLayoutEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-compact', settings.compactMode ? 'true' : 'false');
  }, [settings.compactMode]);

  // Font size — override typography variables via data attribute
  useLayoutEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-font-size', settings.fontSize);
  }, [settings.fontSize]);

  return <>{children}</>;
}
